import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { APP_URL_CHECKOUT, STORAGE_KEY_DATA_FOR_RETRY_PAYMENT, STORAGE_KEY_URL_BEFORE_PAYMENT } from '../const';
import StorageService from '../service/StorageService';
import { commitHeldMessage } from '../service/heldMessage';
import { togglePaymentIssueNotification } from '../store/slices/chatsSlice';
import { toggleUserMenu } from '../store/slices/globalSlice';
import { getPaymentModalOrigin, getPaymentModalSource, setShowPaymentModal } from '../store/slices/modalsSlice';
import { setCurrentPaymentData } from '../store/slices/paymentsSlice';

/**
 * HORO-10478: the one way into `/checkout`.
 *
 * Landing there is not just a navigation — the page reads the purchase out of
 * `currentPaymentData`, and `PaymentResultPage` reads it back out of
 * `STORAGE_KEY_DATA_FOR_RETRY_PAYMENT` to rebuild a failed attempt. Miss either
 * and checkout either redirects home with nothing to sell or "Try again" resumes
 * the wrong purchase. Both are silent failures, which is exactly why this lives
 * in one place rather than being restated at each entry point.
 *
 * Takes the `{ paymentType, paymentId, paymentPrice }` shape the checkout page
 * expects, and clears the surfaces the CTA was tapped from on the way out.
 */
const useGoToCheckout = () => {
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // HORO-10504: a caller that already knows why it's checking out (e.g.
    // ZaraUpsellModal's own anonymous-resume path) tags `paymentData.origin`
    // itself; a caller that goes through the shared PlanPaymentContent/
    // PaymentButton (any logged-in subscribe click routed through the
    // generic PaymentModal) has no way to do that directly, so this falls
    // back to whatever the modal was opened with — see setPaymentModalOrigin.
    const modalOrigin = useSelector(getPaymentModalOrigin);
    // HORO-10635: same fallback for the upsell surface — an explicit
    // `paymentData.source` (direct-to-checkout CTAs) beats the one the modal
    // was opened with. Both ride along into the retry storage below.
    const modalSource = useSelector(getPaymentModalSource);

    return useCallback(
        (paymentData) => {
            const origin = paymentData?.origin || modalOrigin;
            const source = paymentData?.source ?? modalSource;
            const finalPaymentData = {
                ...paymentData,
                ...(origin ? { origin } : {}),
                // `sourceAt` starts the attribution clock — see resolveSubscriptionSource.
                ...(source ? { source, sourceAt: Date.now() } : {})
            };
            dispatch(setCurrentPaymentData(finalPaymentData));
            dispatch(togglePaymentIssueNotification(false));
            dispatch(setShowPaymentModal(false));
            dispatch(toggleUserMenu(false));
            // Where to return the customer once the purchase resolves, and what
            // to rebuild if it fails. Written before navigating so a 3DS
            // full-page redirect can't outrun them.
            StorageService.add(STORAGE_KEY_URL_BEFORE_PAYMENT, pathname);
            StorageService.add(STORAGE_KEY_DATA_FOR_RETRY_PAYMENT, finalPaymentData);
            // HORO-10679: the customer is actually entering checkout, so a
            // message held back by the limit in THIS chat is now worth
            // resuming. The pathname check inside is what keeps a purchase
            // started from the profile or the nav menu from promoting a
            // message held in some unrelated conversation.
            commitHeldMessage(pathname);
            navigate(`/${APP_URL_CHECKOUT}`);
        },
        [dispatch, navigate, pathname, modalOrigin, modalSource]
    );
};

export default useGoToCheckout;
